import type { DrawContext } from './draw';
import type { View, Viewport } from './project';
import { minZoom } from './project';
import type { FloorZooms } from './project';
import { LabelSpace } from './labels';
import type { Box } from './labels';
import { drawBackdrop, drawVoid, voidFade } from './void';
import type { VoidWorld } from './void';
import { drawFarStars } from './far-stars';
import type { FarStar } from './far-stars';
import { drawRungs } from './rungs';
import type { RungWorld } from './rungs';
import { cloudsWanted, drawClouds } from './clouds';
import type { CloudWorld } from './clouds';
import { WashCache, drawWash, washFade } from './wash';
import type { WashCanvas, WashFade, WashPaint, WashTexture } from './wash';
import { drawStars, starDrawCount, starDrift, starStreak, streakCount, streaking } from './stars';
import type { StarZooms } from './stars';
import { drawRealWeb, nodeVisible } from './web';
import type { OffSwitches, WebEdge, WebWorld, WebZooms } from './web';
import type { SkyField } from './field';
import type { EdgeFan } from './fan';
import { drawSky } from './sky-frame';
import type { SkyFrame, SkyFrameResult, SkyFrameZooms } from './sky-frame';
import type { EdgeFocus, EdgePaint } from './sky-edges';
import type { SkyPaint } from './sky-draw';
import { shownNodes } from './nodes';
import type { MapNode, NodeFog, NodeWorld, NodeZooms, ShownNode } from './nodes';
import { drawNodes } from './nodes-draw';
import type { NodePaint } from './nodes-draw';
import { drawWedges, wedgesShowing } from './wedges';
import type { WedgePaint, WedgeZooms } from './wedges';
import { drawNodeLabels } from './node-labels';
import type { LabelState, LabelWeights, LabelZooms } from './node-labels';

/*
 * THE WHOLE STILL PICTURE — twelve layers, in the one order that works.
 *
 * ELEVEN OF THEM ARE THEIRS. The twelfth is the far star field, which stands where their deep dust
 * used to and is the only thing in this file theirs has no counterpart for — see `far-stars.ts`,
 * and `skip.farStars` below for how the differential test still compares the other eleven call for
 * call.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawStatic` (1349–1713).
 *
 * Every layer has its own file and its own differential test. This is the caller, and it is worth
 * its own test for the things no per-layer test can see: a layer left out, a layer run twice, two
 * layers in the wrong order, and — the one that actually bites — CANVAS STATE ONE LAYER LEAVES FOR
 * THE NEXT. A context is a state machine, so the stars' `lineCap` and the rungs' dash and the
 * nodes' `globalAlpha` all outlive the layer that set them.
 *
 * WHY THIS ORDER. It is back to front, and each layer is drawn over the thing it should sit in
 * front of: the void behind the ladder, the ladder behind the unloaded haze, the haze behind the
 * wash, the wash behind the stars, and so on up to the names, which are drawn last because a name
 * lying under a strand is unreadable while a strand under a name is only a strand.
 *
 * THE LABEL SPACE IS SHARED AND ORDERED. One `LabelSpace` is made per frame and passed to three
 * layers in turn — the sky's own names, then the wedges, then the resident names. Asking it for
 * room is taking that room, so the order IS the priority: a wedge name beats a figure's name, and
 * both beat a streamed one. Their `this.tboxes=[]` at line 1403 is where it begins.
 *
 * IT IS CALLED `drawStatic` BECAUSE IT IS CACHED. Their renderer draws this into an offscreen
 * canvas and `drawFrame` blits it, redrawing only what moves. Nothing here knows that; it takes a
 * context and paints.
 */

/** What holds still between frames: the world, the palettes and the thresholds. */
export interface StaticScene {
  /** The world's own vertical scale. Every layer measures against the same one. */
  squash: number;
  void: VoidWorld;
  /** The far field, built once with a seeded generator — see `far-stars.ts`. */
  farStars: readonly FarStar[];
  rungs: RungWorld;
  clouds: CloudWorld;
  wash: { paint: WashPaint; fade: WashFade };
  stars: { field: Float32Array; zooms: StarZooms };
  web: { world: WebWorld; zooms: WebZooms };
  sky: { paint: SkyPaint; zooms: SkyFrameZooms; edges: EdgePaint };
  nodes: { world: NodeWorld; zooms: NodeZooms; paint: NodePaint };
  wedges: { paint: WedgePaint; zooms: WedgeZooms };
  labels: { zooms: LabelZooms; weights: LabelWeights };
  /**
   * HOW FAR OUT THE READER MAY PULL DURING THE ARRIVAL, which is not `minZoom`.
   *
   * On the map the floor comes from `minZoom`; everywhere else it is this ramp, opening as the
   * reader walks. All three numbers are zooms and therefore arguments — the twentieth, twenty-first
   * and twenty-second lifted out of this engine.
   */
  arrival: { floor: number; perVisited: number; floorMin: number;
    /** Where the camera lands on a name walked to, and how far back it stops for one out of reach. */
    arriveAt: number; lookAt: number };
  /** The four zooms the floor is built from — arguments, like every other zoom in the engine. */
  floors: FloorZooms;
  /** Where the naming stops, which `minZoom` measures the whole world against. */
  rim: number;
}

/** What changes every frame. */
export interface StaticFrame {
  view: View;
  /** The spin's raw angle and how much of it is in force — the stars and the depth cue need both. */
  spinAngle: number;
  spinFraction: number;
  dpr: number;
  /** Which beat of the arrival, or `map` once it is over. */
  phase: string;
  /** How fast the camera is moving, in screen pixels a second. */
  speed: number;
  /** Where it is headed, for the star streaks. */
  target: { x: number; y: number };
  /** The reader's own preferences. */
  starDensity: number;
  labelDensity: number;
  /** What they have switched off. */
  off: OffSwitches;
  /** Which name they have open and which is under the pointer. */
  focus: number | null;
  hover: number | null;
  /** Where they have been. */
  visited: ReadonlySet<number>;
  seen: ReadonlySet<number>;
  /** Which ties are soft — inferred rather than stated. Keyed `min:max`. */
  weak?: ReadonlySet<string>;
  /** Whether a name is somewhere they could walk to from where they have been. */
  reachable: (node: MapNode) => boolean;
  /** Whether the arrival is holding the whole field open, and how far the dark has closed back in. */
  revealAll: boolean;
  /** Whether the arrival is still running — the LABEL budget's question, not the fog's. */
  surveying: boolean;
  /** Whether a name the reader cannot reach is still written — see `LabelState.namesOutOfReach`. */
  namesOutOfReach: boolean;
  fogMix: number;
  /** The interface's own rectangles, which no label may sit under. */
  chrome: readonly Box[];
  /** Whether a streamed tile has landed yet. */
  tileReady: (id: string) => boolean;
  /** The resident roster and its ties. */
  nodes: readonly MapNode[];
  edges: readonly WebEdge[];
  /** The streamed field, and the fan that caps how many ties one name may carry. */
  field: SkyField;
  fan: EdgeFan;
  /** How to make the wash's offscreen surface. Passed in, so nothing here touches a DOM. */
  washCanvas: WashCanvas<unknown>;
  /**
   * The profiler's switches, which run a frame with one layer removed to price it.
   *
   * `farStars` earns its place twice: the layer costs a fill a star and the profiler should be able
   * to price it, and it is the ONE layer theirs does not have, so the frame's differential test
   * turns it off to compare the rest of the picture call for call. See `far-stars.ts`.
   */
  skip?: { edges?: boolean; skyEdges?: boolean; skyNodes?: boolean; labels?: boolean; cull?: boolean; lod?: boolean; farStars?: boolean };
}

/** What one still frame drew, for the readout and for the layers that follow it. */
export interface StaticResult {
  /** Every name on screen, in weight order — the picker and the labels both read this. */
  shown: ShownNode[];
  sky: SkyFrameResult;
  /** Strands in the resident web, separately from the streamed one. */
  webStrands: number;
  /** Names written, wedges and figures. */
  wedgeLabels: number;
  nodeLabels: number;
  /** How many names the label budget was measured against. */
  visible: number;
}

/**
 * THEIR ARRIVAL RAMP, as engine.js:1363 states it:
 *
 *   `Math.max(0.13, 0.62 - Math.sqrt(this.visited.size) * 0.055)`
 *
 * The floor opens by a twentieth of a zoom for every name walked, from 0.62 down to 0.13 and no
 * further — which is what makes the field feel like it is widening around a reader as they read.
 *
 * Exported so the wiring and the test read ONE copy. They did not: the test held these three exactly
 * and named them in a comment, while `our-scene.ts` wired 0.055, 0.0016 and 0.02 — the first of them
 * being this table's PER-VISITED, and 0.62 having ended up in a threshold for the presence layer.
 */
export const THEIR_ARRIVAL = { floor: 0.62, perVisited: 0.055, floorMin: 0.13 } as const;

/**
 * How far out the reader may pull, which the void's fade is measured against.
 *
 * Two answers, and their `drawStatic` picks between them on the phase: on the map it is `minZoom`,
 * and during the arrival it is a ramp that opens as the reader walks. Exposed because the fade only
 * means anything relative to it.
 */
export function staticFloor(scene: StaticScene, frame: StaticFrame): number {
  if (frame.phase === 'map') return minZoom(frame.phase, frame.view.view, frame.visited.size, scene.rim, scene.floors);
  return Math.max(scene.arrival.floorMin, scene.arrival.floor - Math.sqrt(frame.visited.size) * scene.arrival.perVisited);
}

/** The wash, built at most once for the life of the caller that owns this cache. */
export class StaticWash {
  private readonly cache: WashCache<unknown>;

  /**
   * Ours fills it from `territory.ts`; leave the builder out and it is theirs, ported, which is
   * what every differential test against their engine wants.
   */
  constructor(build?: (make: WashCanvas<unknown>, paint: WashPaint) => WashTexture<unknown>) {
    this.cache = new WashCache<unknown>(build);
  }

  texture(make: WashCanvas<unknown>, paint: WashPaint): ReturnType<WashCache<unknown>['get']> {
    return this.cache.get(make, paint);
  }

  /** Drop it, for when the roster itself changes. */
  clear(): void {
    this.cache.clear();
  }
}

/**
 * Draw the whole still picture.
 *
 * `wash` is passed in rather than owned so the texture survives between frames — building it per
 * frame is the twenty-nine-screen mistake `wash.ts` exists to prevent.
 */
export function drawStatic(ctx: DrawContext, scene: StaticScene, frame: StaticFrame, wash: StaticWash): StaticResult {
  const view = frame.view;
  const z = view.cam.z;
  const viewport: Viewport = view.view;
  const skip = frame.skip ?? {};

  /* 1 — THE BACKDROP. Resets the matrix to device pixels, so nothing before it can leak in. */
  drawBackdrop(ctx, viewport, frame.dpr);

  /*
   * 2 — BEYOND THE RIM, measured against how far out the reader may pull.
   *
   * The far stars go under the halo, and they are handed no spin: the field turns, the sky behind
   * it does not. That is the whole of what Ariel asked for when the dust that used to be here came
   * out, and it is checked rather than trusted: `static-frame.test.ts` renders the same frame at
   * two spin angles and asks for the same star calls back.
   */
  const floor = staticFloor(scene, frame);
  const beyond = voidFade(z, floor);
  if (skip.farStars !== true) drawFarStars(ctx, view, scene.farStars, beyond);
  drawVoid(ctx, view, scene.void, beyond);

  /*
   * THE LABEL SPACE BEGINS HERE, at their `this.tboxes=[]`. Three layers claim from it in turn and
   * the order is the priority — see the note at the top of this file.
   */
  const labelSpace = new LabelSpace(frame.chrome);

  /* 3 — THE LADDER. */
  drawRungs(ctx, view, scene.rungs);

  /* 4 — WHAT HAS NOT ARRIVED, as haze sized by the count the index already knows. */
  drawClouds(ctx, view, scene.clouds, cloudsWanted(scene.clouds, view, frame.tileReady));

  /* 5 — THE SCRIPTURE WASH, one blit of a texture built once. */
  const fade = washFade(z, scene.wash.fade);
  drawWash(ctx, view, wash.texture(frame.washCanvas, scene.wash.paint), fade, frame.dpr);

  /* 6 — THE STARS, batched by brightness. */
  const streak = starStreak(frame.speed);
  const base = starDrawCount(scene.stars.field, frame.starDensity, z, scene.stars.zooms);
  drawStars(
    ctx,
    view,
    frame.spinAngle,
    scene.stars.field,
    streaking(streak) ? streakCount(base) : base,
    scene.stars.zooms,
    streak,
    starDrift(view, frame.target, streak),
  );

  /* Everything from here shares one frame of the fan, so a name cannot spend its allowance twice. */
  const stamp = frame.fan.reset();
  const visible = (node: MapNode): boolean => nodeVisible(node, frame.off, scene.nodes.world.palette);

  /* 7 — THE RESIDENT WEB. */
  const webStrands = skip.edges === true
    ? 0
    : drawRealWeb(ctx, view, frame.nodes, frame.edges, scene.web.world, scene.web.zooms, frame.fan, stamp, {
        focus: frame.focus,
        hover: frame.hover,
        walked: i => frame.visited.has(i),
        weak: frame.weak,
      });

  /* 8 — THE STREAMED FIELD, at whatever detail this range deserves. */
  const skyFrame: SkyFrame = {
    cull: skip.cull !== true,
    showEdges: skip.edges !== true,
    skipEdges: skip.skyEdges,
    skipNodes: skip.skyNodes,
    useLOD: skip.lod !== true,
  };
  const focus: EdgeFocus = { selected: frame.focus, hovered: frame.hover };
  /*
   * THE FRAME'S OWN STATE, threaded into the paint rather than baked into the scene: which
   * scriptures are switched off, and whether the arrival is still holding the field open. Both
   * were missing, and both are invisible to the sky layer’s own tests — those are handed the right
   * paint. The whole-method comparison found them within a minute of each other.
   */
  const hidden = new Set(scene.sky.paint.corpusKeys.filter(k => frame.off[`c_${k}`] === true));
  const skyPaint: SkyPaint = { ...scene.sky.paint, hidden, reveal: frame.revealAll };
  const sky = drawSky(
    ctx,
    view,
    frame.field,
    skyPaint,
    skyFrame,
    scene.sky.zooms,
    { paint: scene.sky.edges, fan: frame.fan, stamp, focus },
    labelSpace,
  );

  /* 9 — THE RESIDENT NAMES: what there is to paint, then the painting. */
  const fog: NodeFog = {
    visited: frame.visited,
    seen: frame.seen,
    revealAll: frame.revealAll,
    fogMix: frame.fogMix,
  };
  const shown = shownNodes(view, frame.spinFraction, frame.nodes, scene.nodes.world, scene.nodes.zooms, fog, visible);
  /* The reader's filter is a per-FRAME thing, so it is threaded in here rather than baked into the
     scene. Leaving the scene's empty one in place drew every corpus ring at full strength however
     much had been switched off — invisible to the node layer's own test, which is handed the right
     one, and obvious the moment the whole method was compared. */
  drawNodes(ctx, shown, { ...scene.nodes.paint, off: frame.off });

  /* 10 — THE WEDGE NAMES, which claim their room before any figure's does. */
  const wedgeLabels = wedgesShowing(frame.phase, z, scene.wedges.zooms)
    ? drawWedges(ctx, view, scene.wedges.paint, scene.wedges.zooms, labelSpace)
    : 0;

  /* 11 — THE FIGURES' OWN NAMES. Last, because a name under anything is unreadable. */
  const labelState: LabelState = {
    speed: frame.speed,
    density: frame.labelDensity,
    surveying: frame.surveying,
    namesOutOfReach: frame.namesOutOfReach,
    fogMix: frame.fogMix,
    phase: frame.phase,
    floor: minZoom(frame.phase, viewport, frame.visited.size, scene.rim, scene.floors),
    focus: frame.focus,
    reachable: frame.reachable,
    skip: skip.labels,
  };
  const nodeLabels = drawNodeLabels(ctx, shown, viewport, z, labelState, scene.labels.zooms, scene.labels.weights, labelSpace);

  return { shown, sky, webStrands, wedgeLabels, nodeLabels, visible: sky.visible };
}
